/**
 * <FirstLoginPasswordGate> — someone signed in with a password they didn't
 * choose (a legacy shared rank login's initial password, or one the Master
 * set for them) must choose their own before using the app. The server keeps
 * `isFirstLogin` set until they do (PATCH /api/auth/password clears it).
 *
 * Offline the change can't reach the server, so the gate steps aside — crew
 * restoring a session at sea keep working — and comes back once online.
 */
import { useState, type FormEvent } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { KeyRound, Eye, EyeOff, LogOut } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { useOffline } from "@/offline/OfflineProvider";
import { authApi, ApiError } from "@/lib/api-client";

export function FirstLoginPasswordGate({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const { online } = useOffline();
  if (!user?.isFirstLogin || !online) return <>{children}</>;
  return <ChoosePassword />;
}

function ChoosePassword() {
  const { user, refreshUser, logout } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);

  const mismatch = confirm !== "" && confirm !== next;
  const reused = next !== "" && next === current;
  const ok = current.length >= 8 && next.length >= 8 && next === confirm && !reused;

  const mut = useMutation({
    mutationFn: () => authApi.changePassword(current, next),
    onSuccess: async () => {
      toast.success("Password set", { position: "top-center", description: "Use your new password next time you sign in." });
      await refreshUser(); // isFirstLogin is now false — the gate opens
    },
    onError: (err) => toast.error(
      err instanceof ApiError && err.status === 401
        ? "The current password isn't right"
        : err instanceof ApiError ? err.message : "Could not set your password",
      { position: "top-center" },
    ),
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (ok && !mut.isPending) mut.mutate();
  }

  const name = user?.firstName ? `, ${user.firstName}` : "";
  const field = (id: string, label: string, value: string, set: (v: string) => void, opts: { autoFocus?: boolean; autoComplete: string; placeholder?: string }) => (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        type={show ? "text" : "password"}
        value={value}
        onChange={(e) => set(e.target.value)}
        autoComplete={opts.autoComplete}
        autoFocus={opts.autoFocus}
        placeholder={opts.placeholder}
        maxLength={128}
      />
    </div>
  );

  return (
    <div className="grid min-h-svh place-items-center bg-bg p-4">
      <Card className="w-full max-w-md">
        <CardContent className="space-y-5 p-6">
          <div className="space-y-2">
            <div className="grid size-10 place-items-center rounded-full bg-primary/10">
              <KeyRound className="size-5 text-primary" />
            </div>
            <h1 className="text-lg font-semibold text-fg">Choose your own password{name}</h1>
            <p className="text-sm text-fg-muted">
              You signed in with a password someone else set. Choose your own before you carry on — only you should know it.
            </p>
          </div>
          <form onSubmit={onSubmit} className="space-y-4">
            {field("fl-current", "Password you signed in with", current, setCurrent, { autoFocus: true, autoComplete: "current-password" })}
            {field("fl-new", "New password", next, setNext, { autoComplete: "new-password", placeholder: "At least 8 characters" })}
            {field("fl-confirm", "Confirm new password", confirm, setConfirm, { autoComplete: "new-password" })}
            {mismatch ? <p className="text-xs text-danger">The new passwords don't match.</p> : null}
            {reused ? <p className="text-xs text-danger">Choose a password different from the one you were given.</p> : null}
            <button
              type="button"
              onClick={() => setShow((s) => !s)}
              className="inline-flex items-center gap-1.5 text-xs text-fg-muted hover:text-fg"
            >
              {show ? <EyeOff className="size-3.5" /> : <Eye className="size-3.5" />}
              {show ? "Hide passwords" : "Show passwords"}
            </button>
            <Button type="submit" className="w-full" disabled={!ok || mut.isPending} loading={mut.isPending}>
              Set password and continue
            </Button>
          </form>
          <Button variant="ghost" className="w-full" onClick={() => void logout()} disabled={mut.isPending}>
            <LogOut className="mr-1.5 size-4" /> Sign out
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
